import React, { type FC } from "react";
import { type ISearchResultItemModel } from "~/feature/Search/generated-types";
import { AspectRatio, useMediaQuery } from "@chakra-ui/react";
import { mediaApi } from "~/foundation/Jss";
import { imageSizes } from "../../_Base/Theme";
import { LazyImage } from "~/foundation/Components/Image/LazyImage";
import { MotionBox } from "~/foundation/Framer/MotionBox";
import SimpleImagePlaceholder from "../../Media/SimpleImagePlaceholder/SimpleImagePlaceholder";
import { fadeIn } from "../../_Base/Theme/styles/motions";
import { sizes } from "../../_Base/Theme/variables/sizes";
import { Link } from "../../_Base/Components/Link";
import StoryListItemText from "./StoryListItemText.tsx";
import { breakpoints } from "../../_Base/Theme/variables/breakpoints.ts";

export enum AspectRatioOption {
	default = "default",
	cover = "cover",
	auto = "auto"
}

export enum RenderingType {
	resultList = "resultList",
	relatedContentList = "relatedContentList",
}

export type StoryListItemProps = Omit<ISearchResultItemModel, 'date'> & {
	gridColumn?: string;
	aspectRatio?: AspectRatioOption;
	isExternal?: boolean;
	eventEndDate?: string | Date;
	eventCountry?: string;
	date?: string | Date;
	type?: string;
	renderingType?: RenderingType;
}

const StoryListItem: FC<StoryListItemProps> = ({
	url,
	smallImage,
	title,
	tags,
	description,
	gridColumn,
	aspectRatio,
	isExternal,
	eventEndDate,
	eventCountry,
	date,
	type,
	renderingType
}) => {
	const [isMdBreakpointOrAbove] = useMediaQuery(`(min-width: ${breakpoints.md})`);

	// Get img aspect ratio
	const imageW = Number(smallImage?.width) || 4;
	const imageH = Number(smallImage?.height) || 3;

	let ratio = 4 / 3;
	if (aspectRatio === AspectRatioOption.cover) {
		ratio = 3 / 4;
	} else if (aspectRatio === AspectRatioOption.auto) {
		// calc aspect ratio from dimensions, fallback 4 / 3
		ratio = imageW / imageH;
	}

	const remToPx = (remValue: string, base: number = 16): number => {
		const numericValue = parseFloat(remValue.replace('rem', '').trim());
		return numericValue * base;
	};

	const contentWrapperWidthPx = remToPx(String(sizes.contentWrapperWidthXL));

	const getSrcSet = () => {
		if (aspectRatio === AspectRatioOption.cover) {
			return imageSizes.review.md;
		} else if (aspectRatio === AspectRatioOption.auto) {
			const baseWidth = contentWrapperWidthPx / 4;
			const baseHeight = baseWidth / ratio;

			return {
				cw: baseWidth,
				ch: baseHeight
			};
		}
		return imageSizes.articleList.sm;
	};

	const srcSet = getSrcSet();

	const isReview = type === "Reviews";

	// Return specific layout for related content list if the type is reviews
	// Reviews have different layout, different aspect ratios etc.
	if (renderingType === RenderingType.relatedContentList && isReview && isMdBreakpointOrAbove) {
		return (
			<MotionBox
				as="li"
				listStyleType="none"
				variants={fadeIn}
				gridColumn={gridColumn || "auto"}
			>
				<Link
					to={url}
					target={isExternal ? "_blank" : undefined}
					variant="unstyled"
					_focus={{ outlineOffset: "0.5rem" }}
					display="flex"
					noWrapper={isReview}
					w="100%"
					gap={4}
					alignItems="flex-start"
				>
					<AspectRatio
						ratio={ratio}
						h="auto"
						w="100%"
						overflow="hidden"
						boxShadow={isReview ? "0px 5px 12px 0px rgba(0, 0, 0, 0.15)" : "none"}
					>
						{smallImage?.url ? (
							<LazyImage
								alt={smallImage.alt}
								src={mediaApi.updateImageUrl(smallImage?.url, srcSet)}
								w="auto"
								h="100%"
								sx={{
									objectFit: aspectRatio === AspectRatioOption.auto ? "contain !important" : "cover",
									objectPosition: aspectRatio === AspectRatioOption.auto ? "top left" : "center center",
								}}
							/>
						) : (
							<SimpleImagePlaceholder missingAltText={title || "result"} />
						)}
					</AspectRatio>

					<StoryListItemText
						title={title}
						tags={tags}
						description={description}
						eventEndDate={eventEndDate}
						eventCountry={eventCountry}
						date={date}
					/>
				</Link>
			</MotionBox>
		);
	}

	// Normal layout for results list - other lists
	return (
		<MotionBox
			as="li"
			listStyleType="none"
			variants={fadeIn}
			gridColumn={gridColumn || "auto"}
		>
			<Link
				to={url}
				target={isExternal ? "_blank" : undefined}
				variant="unstyled"
				display="block"
				minW="100%"
				maxW="100%"
				_focus={{ outlineOffset: "0.5rem" }}
			>
				<AspectRatio ratio={ratio} h={aspectRatio === AspectRatioOption.auto ? "auto" : "auto"} overflow="hidden" mb={3} boxShadow={isReview ? "0px 5px 12px 0px rgba(0, 0, 0, 0.15)" : "none"}>
					{smallImage?.url ? (
						<LazyImage
							alt={smallImage.alt}
							src={mediaApi.updateImageUrl(smallImage?.url, srcSet)}
							w="100%"
							h="100%"
							sx={{
								objectFit: aspectRatio === AspectRatioOption.auto ? "contain !important" : "cover",
								objectPosition: aspectRatio === AspectRatioOption.auto ? "bottom" : "center center",
							}}
						/>
					) : (
						<SimpleImagePlaceholder missingAltText={title || "result"} />
					)}
				</AspectRatio>

				<StoryListItemText
					title={title}
					tags={tags}
					description={description}
					eventEndDate={eventEndDate}
					eventCountry={eventCountry}
					date={date}
				/>
			</Link>
		</MotionBox>
	);
};

export default StoryListItem;